import { Component, inject, input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { joinRoleLabels } from './join-role-labels';

/**
 * A person's roles as small chips, for a header or a menu (the user menu and the profile page): at most `max` chips, then a "+N" chip whose tooltip lists the rest, or
 * every role wrapping onto more lines when `max` is left out. No separators: each role is its own chip, so a line break never leaves a "·" at the start of a line, in
 * either direction. It has no overlay (it is cheap enough for the app's first bundle); the table's one-line variant with the popover is RoleChipsComponent.
 */
@Component({
  selector: 'app-role-chip-list',
  standalone: true,
  template: `
    <span class="chips">
      @for (label of visible(); track $index) {
        <span class="chip" data-testid="role-chip" [attr.title]="label">{{ label }}</span>
      }
      @if (hiddenCount() > 0) {
        <span class="chip more" data-testid="role-more" tabindex="0" [attr.title]="hiddenText()" [attr.aria-label]="hiddenText()"
          ><span dir="ltr">+{{ hiddenCount() }}</span></span
        >
      }
    </span>
  `,
  styles: `
    :host {
      display: block;
      max-width: 100%;
    }
    .chips {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 4px;
    }
    .chip {
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      padding: 2px 8px;
      border-radius: var(--radius-full);
      background: var(--neutral-status-soft);
      color: var(--ink);
      font-family: var(--font-display);
      font-size: 0.72rem;
      line-height: 1.5;
    }
    .more {
      border: 1px solid var(--line);
      background: var(--panel);
      color: var(--ink-soft);
      font-weight: 600;
      cursor: default;
    }
    .more:focus-visible {
      outline: none;
      box-shadow: var(--shadow-focus);
    }
  `
})
export class RoleChipListComponent {
  readonly roles = input<readonly string[]>([]);
  /** How many chips show before the "+N" chip; unset, every role shows. */
  readonly max = input<number | null>(null);

  private readonly translate = inject(TranslateService);

  /** Every role, as the viewer reads it (read on each pass, so a language switch shows at once). */
  private labels(): string[] {
    return this.roles().map((role) => this.translate.instant('shared.roles.' + role) as string);
  }

  visible(): string[] {
    const max = this.max();
    return max === null ? this.labels() : this.labels().slice(0, max);
  }

  hiddenCount(): number {
    return this.roles().length - this.visible().length;
  }

  /** The roles behind the "+N" chip, as text in the viewer's language (its tooltip). */
  hiddenText(): string {
    return joinRoleLabels(this.labels().slice(this.visible().length), this.translate.currentLang());
  }
}
